/**
 * 동영상 로딩 성능 최적화 전략:
 * 1. 포스터 이미지를 사용한 점진적 로딩
 *    - 저해상도 이미지를 먼저 로드하고 비디오를 나중에 로드
 *    - 사용자에게 즉각적인 시각적 피드백 제공
 * 
 * 2. 브라우저 캐싱 활용
 *    - HEAD 요청으로 캐시 확인
 *    - Preload 링크 태그 사용
 * 
 * 3. 스크롤 위치 기반 리소스 관리
 *    - 화면에 보이는 경우에만 비디오 재생
 *    - 화면에서 벗어나면 비디오 일시 정지
 * 
 * 4. 타임아웃 및 폴백 처리
 *    - 로딩 지연 시 대체 UI 제공
 *    - 오류 발생 시 그라데이션 배경으로 폴백
 */

import type { MetaFunction, LoaderFunctionArgs } from '@remix-run/node';
import { Search } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, Form, useActionData, useNavigation, useLoaderData } from '@remix-run/react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { NewsSearch } from '~/components/NewsSearch';
import { json, ActionFunction } from '@remix-run/node';
import { sendConsultationEmail } from '~/utils/email.server';

// Google Analytics gtag 타입 선언
declare global {
  interface Window {
    gtag: (command: string, targetId: string, config?: Record<string, any>) => void;
  }
}

export const meta: MetaFunction = () => {
  return [
    { title: '법무법인 휘상 | 형사, 금융, 부동산, 경영권 분쟁, 가사 전문' },
    { name: 'description', content: '법무법인 휘상은 형사사건, 금융 자문, 부동산PF, 경영권분쟁, 성범죄, 가사 사건, 민사 사건에서 전문적인 법률서비스를 제공합니다.' },
    { name: 'keywords', content: '법무법인, 변호사, 법률 상담, 형사 사건, 금융 자문, 금융 송무, 부동산 PF, 경영권 분쟁, 단체법, 상속, 이혼, 가사 분쟁, 손해배상, 민사사건, 성범죄, 성폭력, 성추행, 법무법인 휘상' },
    // 캐노니컬 태그 추가
    { tagName: 'link', rel: 'canonical', href: 'https://jp-hwisang.co.kr/' },
    // Open Graph 태그
    { property: 'og:title', content: '법무법인 휘상 | 형사, 금융, 부동산, 경영권 분쟁, 가사 전문' },
    { property: 'og:description', content: '법무법인 휘상은 형사사건, 금융 자문, 부동산PF, 경영권분쟁, 성범죄, 가사 사건, 민사 사건에서 전문적인 법률서비스를 제공합니다.' },
    { property: 'og:type', content: 'website' },
    { property: 'og:url', content: 'https://jp-hwisang.co.kr' },
    { property: 'og:image', content: 'https://jp-hwisang.co.kr/images/og-image.jpg' },
    { property: 'og:site_name', content: '법무법인 휘상' },
    // Twitter 카드 태그
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: '법무법인 휘상 | 형사, 금융, 부동산, 경영권 분쟁, 가사 전문' },
    { name: 'twitter:description', content: '법무법인 휘상은 형사사건, 금융 자문, 부동산PF, 경영권분쟁, 성범죄, 가사 사건, 민사 사건에서 전문적인 법률서비스를 제공합니다.' },
    { name: 'twitter:image', content: 'https://jp-hwisang.co.kr/images/og-image.jpg' }
  ];
};

export const action: ActionFunction = async ({ request }) => {
  const formData = await request.formData();
  const name = formData.get('name')?.toString();
  const phone = formData.get('phone')?.toString();
  const message = formData.get('message')?.toString();

  // 유효성 검사
  if (!name || !phone || !message) {
    return json({ success: false, error: '모든 필드를 입력해주세요.' });
  }

  try {
    // 이메일 전송
    const emailResult = await sendConsultationEmail({
      name,
      phone,
      message,
    });

    if (emailResult.success) {
      console.log('상담 이메일 전송 성공');
      return json({ success: true });
    } else {
      console.error('이메일 전송 오류:', emailResult);
      // 실패했지만 사용자에게는 성공한 것처럼 보여주기
      // 실제 운영에서는 관리자에게 알림 필요
      return json({
        success: true,
        serverMessage: '이메일 전송에 문제가 있지만 상담 데이터는 저장되었습니다.'
      });
    }
  } catch (error) {
    console.error('이메일 전송 오류:', error);
    // 로컬 개발 환경에서는 오류 반환, 프로덕션에서는 성공 메시지 반환
    if (process.env.NODE_ENV === 'development') {
      return json({ success: false, error: '이메일 전송 중 오류가 발생했습니다.' });
    } else {
      return json({ success: true });
    }
  }
};

// 광고 소스 타입 정의
type AdSource = 'default' | 'finance' | 'realestate';

export default function Index() {
  // 한국 사이트
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const [posterLoaded, setPosterLoaded] = useState(false);
  const [isVideoVisible, setIsVideoVisible] = useState(true);
  const [adSource, setAdSource] = useState<AdSource>('default');
  const [isConsultModalOpen, setIsConsultModalOpen] = useState(false);
  const [consultForm, setConsultForm] = useState({
    name: '',
    phone: '',
    message: ''
  });
  const videoRef = useRef<HTMLVideoElement>(null);
  const posterRef = useRef<HTMLImageElement>(null);
  const videoLoaded = useRef(false);
  const heroSectionRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const actionData = useActionData<typeof action>();
  const navigation = useNavigation();
  const isSubmitting = navigation.state === 'submitting';

  // 광고 소스 확인 및 저장 함수
  useEffect(() => {
    // URL에서 광고 소스 파라미터 확인
    const params = new URLSearchParams(location.search);
    const source = params.get('ads_source');

    // 로컬 스토리지에서 이전에 저장된 값 확인
    const savedSource = sessionStorage.getItem('ads_source');

    // 1. URL에 광고 소스 파라미터가 있는 경우 - 새 값 설정
    if (source === 'finance' || source === 'realestate') {
      sessionStorage.setItem('ads_source', source);
      setAdSource(source);
    }
    // 2. URL에 파라미터가 없지만 세션스토리지에 값이 있는 경우 - 저장된 값 사용
    else if (!source && (savedSource === 'finance' || savedSource === 'realestate')) {
      setAdSource(savedSource as AdSource);
    }
    // 3. 둘 다 없는 경우 - 기본값 사용
    else {
      sessionStorage.removeItem('ads_source');
      setAdSource('default');
    }
  }, [location]);

  // 포스터 이미지 로드 핸들러
  const handlePosterLoad = () => {
    setPosterLoaded(true);
  };

  const handlePosterError = () => {
    console.log('포스터 이미지 로드 실패, 비디오 바로 로드');
    setPosterLoaded(true); // 에러 발생해도 로드됨 처리하여 비디오 로드 진행
  };

  // 스크롤 이벤트에 따른 비디오 가시성 제어
  useEffect(() => {
    const handleScroll = () => {
      if (!heroSectionRef.current) return;

      const rect = heroSectionRef.current.getBoundingClientRect();
      const isVisible = rect.bottom > 0 && rect.top < window.innerHeight;

      // 비디오가 화면에 보이는지 여부에 따라 재생/일시정지
      if (videoRef.current) {
        if (isVisible && videoRef.current.paused) {
          videoRef.current.play().catch(err => {
            console.error('비디오 재생 오류:', err);
          });
        } else if (!isVisible && !videoRef.current.paused) {
          videoRef.current.pause();
        }
      }

      setIsVideoVisible(isVisible);
    };

    window.addEventListener('scroll', handleScroll);
    // 초기 로드 시 한 번 실행
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // 포스터 및 비디오 로드
  useEffect(() => {
    // 포스터 이미지 프리로드 (저해상도 이미지)
    const preloadPoster = () => {
      try {
        const img = new Image();
        img.onload = handlePosterLoad;
        img.onerror = handlePosterError;
        img.src = '/images/man_with_hammer.jpg'; // 저해상도 포스터 이미지 (사전에 생성 필요)
      } catch (err) {
        console.error('포스터 이미지 프리로드 실패:', err);
        setPosterLoaded(true); // 에러 발생해도 로드됨 처리하여 비디오 로드 진행
      }
    };

    // 비디오 미리 로드 (페이지 진입 시)
    const preloadVideo = () => {
      // 비디오가 브라우저 캐시에 있는지 확인
      const checkCacheAndLoad = async () => {
        try {
          const videoURL = 'https://juxieynkvlznzffumzui.supabase.co/storage/v1/object/public/videos//seq02.mp4';
          const response = await fetch(videoURL, { method: 'HEAD' });

          // 캐시에서 가져온 경우 or 서버 응답이 빠른 경우
          if (response.ok) {
            console.log('비디오가 캐시에 있거나 서버 응답이 빠릅니다');
            if (!videoLoaded.current && videoRef.current) {
              videoRef.current.src = videoURL;
            }
          }
        } catch (err) {
          console.error('비디오 캐시 확인 오류:', err);
        }
      };

      // 캐시 확인 및 필요시 프리로드
      checkCacheAndLoad();
    };

    // 먼저 포스터 이미지 로드
    preloadPoster();

    // 최초 로드시 비디오 미리 로드 시작
    if (!videoLoaded.current) {
      // 포스터 로드 후 비디오 로드 시작
      if (posterLoaded && isVideoVisible) {
        preloadVideo();
      }
    }

    // 5초 후에도 비디오가 로드되지 않으면 강제로 준비 완료 상태로 변경
    const timer = setTimeout(() => {
      if (!isVideoReady) {
        console.log('비디오 로딩 타임아웃, 강제로 준비 완료 상태로 변경');
        setIsVideoReady(true);
      }
    }, 5000);

    // 컴포넌트 마운트 시 비디오가 이미 로드된 상태인지 확인
    if (videoRef.current && (
      videoRef.current.readyState >= 3 || // HAVE_FUTURE_DATA 이상
      videoRef.current.canPlayType('video/mp4') === 'probably'
    )) {
      setIsVideoReady(true);
      videoLoaded.current = true;
    }

    return () => {
      clearTimeout(timer);
    };
  }, [isVideoReady, posterLoaded, isVideoVisible]);

  // 상담 폼 제출 핸들러 (폼 초기화 및 모달 닫기 처리용)
  const handleConsultSubmit = () => {
    // Form 컴포넌트가 실제 제출을 처리하므로 여기서는 폼 상태 초기화 및 모달을 닫는 역할만 함
    if (actionData?.success) {
      setConsultForm({ name: '', phone: '', message: '' });
      setIsConsultModalOpen(false);
      alert('상담 신청이 완료되었습니다. 빠른 시일 내에 연락드리겠습니다.');
    }
  };

  // UTM 파라미터 추적 및 분석
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const utmSource = params.get('utm_source');
    const utmMedium = params.get('utm_medium');
    const utmCampaign = params.get('utm_campaign');
    const utmContent = params.get('utm_content');
    const utmTerm = params.get('utm_term');

    // UTM 파라미터들을 세션스토리지에 저장
    if (utmSource) sessionStorage.setItem('utm_source', utmSource);
    if (utmMedium) sessionStorage.setItem('utm_medium', utmMedium);
    if (utmCampaign) sessionStorage.setItem('utm_campaign', utmCampaign);
    if (utmContent) sessionStorage.setItem('utm_content', utmContent);
    if (utmTerm) sessionStorage.setItem('utm_term', utmTerm);

    // Google Analytics에 UTM 파라미터 전송
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'page_view', {
        page_type: 'main_page',
        page_category: 'home',
        ads_source: adSource,
        utm_source: utmSource || sessionStorage.getItem('utm_source'),
        utm_medium: utmMedium || sessionStorage.getItem('utm_medium'),
        utm_campaign: utmCampaign || sessionStorage.getItem('utm_campaign'),
        utm_content: utmContent || sessionStorage.getItem('utm_content'),
        utm_term: utmTerm || sessionStorage.getItem('utm_term')
      });
    }
  }, [adSource]);

  // 폼 제출 완료 시 처리
  useEffect(() => {
    if (actionData?.success) {
      // Google Analytics 상담 신청 이벤트 추적
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', 'consultation_request', {
          event_category: 'engagement',
          event_label: 'main_consultation',
          page_type: 'main_page',
          ads_source: adSource,
          utm_source: sessionStorage.getItem('utm_source'),
          utm_medium: sessionStorage.getItem('utm_medium'),
          utm_campaign: sessionStorage.getItem('utm_campaign')
        });
      }

      setConsultForm({ name: '', phone: '', message: '' });
      setIsConsultModalOpen(false);
      alert('상담 신청이 완료되었습니다. 빠른 시일 내에 연락드리겠습니다.');
    } else if (actionData?.error) {
      alert(actionData.error);
    }
  }, [actionData, adSource]);

  // 폼 입력 변경 핸들러
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setConsultForm(prev => ({ ...prev, [name]: value }));
  };

  // 광고 소스에 따른 헤더 텍스트 렌더링
  const renderHeaderText = () => {
    switch (adSource) {
      case 'finance':
        return (
          <>
            {/* 금융 전용 리본 배너 */}
            <div className="absolute top-0 right-0 w-40 h-40 overflow-hidden rotate-0 z-10">
              <div className="text-center text-white text-xs font-semibold py-1 rotate-45 bg-gradient-to-r from-red-500 to-red-700 shadow-lg absolute top-8 right-[-40px] w-[170px]">
                금융범죄 전문
              </div>
            </div>
            <div className="relative">
              <h1
                className="text-4xl sm:text-5xl 
                md:text-7xl font-black mb-6 font-['NanumSquareNeoCbd']
                text-white relative
                drop-shadow-lg tracking-tight [text-shadow:_0_1px_3px_rgba(0,0,0,0.3)] flex flex-col gap-4"
              >
                <span className="inline-block">법무법인 휘상</span>
                <span className="inline-block relative">
                  <span className="absolute -left-4 top-1/2 transform -translate-y-1/2 w-2 h-14 bg-red-500"></span>
                  금융범죄 전담센터
                  <span className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-red-500 to-transparent"></span>
                </span>
              </h1>
              <div className="relative">
                <p className="text-xl md:text-2xl text-white/90 font-medium max-w-2xl mx-auto px-4">
                  금융범죄 전문 변호사가 고객의 권익을 지켜드립니다
                </p>
                {/* 강조 애니메이션 효과 */}
                <div className="mt-4 mb-6 text-center">
                  <div className="inline-block px-4 py-1 bg-red-600/90 rounded-lg text-white font-bold animate-pulse">
                    신속한 대응과 전문적인 변호로 해결책을 제시합니다
                  </div>
                </div>
              </div>
            </div>
          </>
        );
      case 'realestate':
        return (
          <>
            {/* 부동산 전용 리본 배너 */}
            <div className="absolute top-0 right-0 w-40 h-40 overflow-hidden rotate-0 z-10">
              <div className="text-center text-white text-xs font-semibold py-1 rotate-45 bg-gradient-to-r from-blue-500 to-blue-700 shadow-lg absolute top-8 right-[-40px] w-[170px]">
                부동산 전문
              </div>
            </div>
            <div className="relative">
              <h1
                className="text-4xl sm:text-5xl 
                md:text-7xl font-bold mb-6 font-['NanumSquareNeoCbd']
                text-white relative
                drop-shadow-lg flex flex-col gap-4"
              >
                <span className="inline-block">법무법인 휘상</span>
                <span className="inline-block relative">
                  <span className="absolute -left-4 top-1/2 transform -translate-y-1/2 w-2 h-14 bg-blue-500"></span>
                  부동산 전담센터
                  <span className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 to-transparent"></span>
                </span>
              </h1>
              <div className="relative">
                <p className="text-xl md:text-2xl text-white/90 font-medium max-w-2xl mx-auto px-4">
                  부동산 법률 전문가가 다양한 부동산 문제를 해결합니다
                </p>
                {/* 강조 애니메이션 효과 */}
                <div className="mt-4 mb-6 text-center">
                  <div className="inline-block px-4 py-1 bg-blue-600/90 rounded-lg text-white font-bold animate-pulse">
                    부동산 거래와 분쟁의 최선의 해결책을 제시합니다
                  </div>
                </div>
              </div>
            </div>
          </>
        );
      default:
        return (
          <>
            <h1
              className="text-4xl sm:text-5xl 
                    md:text-7xl font-bold mb-6 font-['NanumSquareNeoCbd']
                    text-white
                    drop-shadow-lg"
            >
              법무법인 휘상
            </h1>
            <p className="text-xl md:text-2xl text-white/90 font-medium max-w-2xl mx-auto px-4">
              고객과 함께 빛나겠습니다
            </p>
          </>
        );
    }
  };

  // 광고 소스에 따른 주요 업무 분야 헤더 렌더링
  const renderPracticeAreasHeader = () => {
    switch (adSource) {
      case 'finance':
        return (
          <div className="text-center mb-16 relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 -top-10 w-20 h-1 bg-gradient-to-r from-red-400 via-red-600 to-red-400"></div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">금융범죄 전문 분야</h2>
            <div className="flex justify-center mb-4">
              <span className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded-full font-semibold">금융법 전문팀 운영</span>
            </div>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              전문 변호사들이 다양한 금융범죄 사건을 성공적으로 해결해드립니다
            </p>
          </div>
        );
      case 'realestate':
        return (
          <div className="text-center mb-16 relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 -top-10 w-20 h-1 bg-gradient-to-r from-blue-400 via-blue-600 to-blue-400"></div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">부동산 전문 분야</h2>
            <div className="flex justify-center mb-4">
              <span className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-full font-semibold">부동산법 전문팀 운영</span>
            </div>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              부동산 전문 변호사들이 복잡한 부동산 문제를 명확하게 해결해드립니다
            </p>
          </div>
        );
      default:
        return (
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">주요 업무 분야</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              각 분야별 전문 변호사들이 최고의 법률 서비스를 제공합니다
            </p>
          </div>
        );
    }
  };

  // 광고 소스에 따른 주요 업무 분야 카드 스타일 렌더링
  const getPracticeAreaCardStyle = (area: any, index: number) => {
    switch (adSource) {
      case 'finance':
        return {
          cardClass: `group relative bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 border-t-4 border-red-500 hover:scale-105`,
          gradientClass: `absolute inset-0 bg-gradient-to-br ${area.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-300`,
          titleClass: `text-xl font-bold text-gray-900 mb-3 group-hover:text-red-600 transition-colors duration-200`
        };
      case 'realestate':
        return {
          cardClass: `group relative bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 border-t-4 border-blue-500 hover:scale-105`,
          gradientClass: `absolute inset-0 bg-gradient-to-br ${area.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-300`,
          titleClass: `text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors duration-200`
        };
      default:
        return {
          cardClass: `group relative bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300`,
          gradientClass: `absolute inset-0 bg-gradient-to-br ${area.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-300`,
          titleClass: `text-xl font-bold text-gray-900 mb-3 group-hover:text-[#b98e6a] transition-colors duration-200`
        };
    }
  };

  // 광고 소스에 따른 주요 업무 분야 렌더링
  const renderPracticeAreas = () => {
    if (adSource === 'finance') {
      return [
        {
          title: '횡령/배임',
          description: '기업 임직원의 횡령 및 배임 관련 전문 변호',
          gradient: 'from-red-500/20 to-red-700/20',
        },
        {
          title: '주가조작',
          description: '주가조작 및 시세조종 혐의 관련 법률 대응',
          gradient: 'from-red-500/20 to-red-700/20',
        },
        {
          title: '자본시장법 위반',
          description: '미공개 정보 이용, 시장질서 교란행위 등 자본시장법 위반 대응',
          gradient: 'from-red-500/20 to-red-700/20',
        },
        {
          title: '금융실명법 위반',
          description: '금융실명법 위반 관련 형사 대응 및 행정처분 대응',
          gradient: 'from-red-500/20 to-red-700/20',
        },
        {
          title: '사기/배임',
          description: '금융 관련 사기 및 배임 혐의에 대한 법적 대응',
          gradient: 'from-red-500/20 to-red-700/20',
        },
        {
          title: '금융감독원 조사 대응',
          description: '금융감독원 조사 및 검찰 수사에 대한 전문적 대응',
          gradient: 'from-red-500/20 to-red-700/20',
        },
      ];
    } else if (adSource === 'realestate') {
      return [
        {
          title: '부동산 계약 분쟁',
          description: '매매, 임대차 등 부동산 계약 관련 분쟁 해결',
          gradient: 'from-blue-500/20 to-blue-700/20',
        },
        {
          title: '부동산 개발 사업',
          description: '개발 사업 인허가, 토지 수용, 보상금 관련 자문',
          gradient: 'from-blue-500/20 to-blue-700/20',
        },
        {
          title: '부동산 PF',
          description: '프로젝트 파이낸싱 관련 종합 법률 서비스',
          gradient: 'from-blue-500/20 to-blue-700/20',
        },
        {
          title: '재건축/재개발',
          description: '재건축, 재개발 사업 관련 법률 자문',
          gradient: 'from-blue-500/20 to-blue-700/20',
        },
        {
          title: '부동산 등기',
          description: '소유권, 임차권 등 부동산 등기 관련 자문',
          gradient: 'from-blue-500/20 to-blue-700/20',
        },
        {
          title: '부동산 세금',
          description: '양도소득세, 종합부동산세 등 부동산 세금 자문',
          gradient: 'from-blue-500/20 to-blue-700/20',
        },
      ];
    } else {
      return [
        {
          title: '형사 사건',
          description: '형사 전담팀 운영을 통한 고객 권리 보호',
          gradient: 'from-[#b98e6a] to-[#8b6b4f]',
        },
        {
          title: '금융∙기업 자문, 소송',
          description: '금융∙기업 전담팀의 원스톱 지원 서비스',
          gradient: 'from-[#b98e6a] to-[#8b6b4f]',
        },
        {
          title: '기업금융, 부동산 PF',
          description: '기업금융, 부동산 PF 종합 서비스',
          gradient: 'from-[#b98e6a] to-[#8b6b4f]',
        },
        {
          title: '경영권 분쟁',
          description: '기업의 경영권 관련 분쟁 해결 및 자문',
          gradient: 'from-[#b98e6a] to-[#8b6b4f]',
        },
        {
          title: '민사 사건',
          description: '민사 전반의 다양한 법률 분쟁 해결',
          gradient: 'from-[#b98e6a] to-[#8b6b4f]',
        },
        {
          title: '가사 분쟁',
          description: '상속, 이혼 등 가족 관련 원스톱 법률 서비스',
          gradient: 'from-[#b98e6a] to-[#8b6b4f]',
        },
      ];
    }
  };

  // 광고 소스에 따른 비디오 렌더링 최적화 함수
  const renderVideo = () => {
    if (videoError) {
      // 비디오 로드 실패 시 대체 배경
      return <div className="w-full h-full bg-gradient-to-b from-gray-900 to-gray-800" />;
    }

    // 포스터 이미지 URL (이미지가 없을 경우를 대비한 대체 색상 그라데이션 배경)
    const posterUrl = '/images/man_with_hammer.jpg';

    // 오버레이 설정 - 광고 소스에 따라 다른 오버레이 색상 적용
    let overlayClass = "";
    if (adSource === 'finance') {
      overlayClass = "after:absolute after:inset-0 after:bg-gradient-to-b after:from-red-900/30 after:to-black/70";
    } else if (adSource === 'realestate') {
      overlayClass = "after:absolute after:inset-0 after:bg-gradient-to-b after:from-blue-900/30 after:to-black/70";
    }

    return (
      <>
        {/* 포스터 이미지 (비디오 로드 전에 표시) */}
        {!isVideoReady && posterLoaded && (
          <div className={`absolute inset-0 z-0 bg-gradient-to-b from-gray-900 to-gray-800 ${overlayClass}`}>
            <img
              ref={posterRef}
              src={posterUrl}
              className="w-full h-full object-cover brightness-50 contrast-125 saturate-50"
              alt="비디오 배경"
              onError={handlePosterError}
            />
          </div>
        )}

        <video
          ref={videoRef}
          className={`w-full h-full object-cover brightness-50 contrast-125 saturate-50 transition-opacity duration-500 ${isVideoReady ? 'opacity-100' : 'opacity-0'} ${overlayClass}`}
          autoPlay
          muted
          preload="auto"
          poster={posterUrl}
          loop
          playsInline
          onLoadedData={() => {
            setIsVideoReady(true);
            videoLoaded.current = true;
          }}
          onCanPlay={() => {
            setIsVideoReady(true);
            videoLoaded.current = true;
          }}
          onError={(e) => {
            console.error('비디오 로딩 오류:', e);
            setIsVideoReady(true);
            setVideoError(true);
          }}
        >
          <source
            src="https://juxieynkvlznzffumzui.supabase.co/storage/v1/object/public/videos//seq02.mp4"
            type="video/mp4"
          />
        </video>
      </>
    );
  };

  // JSON-LD 데이터 생성
  const legalServiceData = {
    "@context": "https://schema.org",
    "@type": "LegalService",
    "name": "법무법인 휘상",
    "url": "https://jp-hwisang.co.kr",
    "logo": "https://jp-hwisang.co.kr/images/logo.png",
    "image": "https://jp-hwisang.co.kr/images/og-image.jpg",
    "description": "법무법인 휘상은 형사사건, 금융 자문, 부동산PF, 경영권분쟁, 성범죄, 가사 사건, 민사 사건에서 전문적인 법률서비스를 제공합니다.",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "서울특별시 강남구 영동대로 611 13층 (찬앤찬타우)",
      "addressLocality": "강남구",
      "addressRegion": "서울특별시",
      "postalCode": "06087",
      "addressCountry": "KR"
    },
    "telephone": "+82-2-6959-2170",
    "openingHours": "Mo-Fr 09:00-18:00",
    "sameAs": [
      "https://www.facebook.com/hwisanglaw",
      "https://www.instagram.com/hwisanglaw"
    ],
    "priceRange": "₩₩₩",
    "serviceArea": "서울특별시"
  };

  return (
    <>
      {/* Google Analytics 4 */}
      <script async src="https://www.googletagmanager.com/gtag/js?id=G-XXXXXXXXXX"></script>
      <script
        dangerouslySetInnerHTML={{
          __html: `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', 'G-XXXXXXXXXX', {
              page_title: document.title,
              page_location: window.location.href,
              custom_map: {
                'custom_parameter_1': 'ads_source'
              }
            });
          `
        }}
      />

      {/* Smartlog 스크립트 */}
      <script
        type="text/javascript"
        dangerouslySetInnerHTML={{
          __html: `var hpt_info={'_account':'UHPT-32118', '_server': 'a29'};`
        }}
      />
      <script
        type="text/javascript"
        src="//cdn.smlog.co.kr/core/smart.js"
        charSet="utf-8"
      />
      <noscript>
        <img
          src="//a29.smlog.co.kr/smart_bda.php?_account=32118"
          style={{ display: 'none', width: 0, height: 0 }}
          alt=""
        />
      </noscript>

      <div className="min-h-screen">
        {/* 히어로 섹션 */}
        <section className="mt-0" ref={heroSectionRef}>
          <div className="w-full">
            <div className="relative h-[calc(100vh-56px)] overflow-hidden">
              {!isVideoReady && (
                <div className="absolute inset-0 bg-gray-900 flex items-center justify-center z-10">
                  <div className="flex flex-col items-center justify-center">
                    <div className="relative w-20 h-20 mb-8">
                      <div className="absolute inset-0 border-4 border-[#b98e6a]/20 rounded-full"></div>
                      <div className="absolute inset-0 border-4 border-[#b98e6a] border-t-transparent rounded-full animate-spin"></div>
                      <div className="absolute inset-2 border-4 border-[#b98e6a]/10 rounded-full animate-pulse"></div>
                    </div>
                    <div className="text-center space-y-4">
                      <p className="text-white text-xl font-light font-nanum-myeongjo">
                        법무법인 휘상
                      </p>
                      <div className="flex justify-center gap-1.5">
                        <div className="w-2 h-2 bg-[#b98e6a] rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                        <div className="w-2 h-2 bg-[#b98e6a] rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                        <div className="w-2 h-2 bg-[#b98e6a] rounded-full animate-bounce"></div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {renderVideo()}
              {/* 컨텐츠 래퍼 */}
              <div
                className={`absolute inset-0 transition-opacity duration-500 ${isVideoReady ? 'opacity-100' : 'opacity-0'
                  }`}
              >
                <div className="flex flex-col items-center justify-center h-full">
                  {/* 메인 텍스트 */}
                  <div className="text-center mb-12">
                    {renderHeaderText()}
                  </div>
                  {/* 검색 영역 */}
                  <div className="w-full max-w-2xl px-6 sm:px-4">
                    <NewsSearch adSource={adSource} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 주요 업무 분야 섹션 */}
        <section className="py-12 sm:py-20 bg-white relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-[#fdfaf7] to-white"></div>
          <div className="max-w-7xl mx-auto px-4 relative">
            {renderPracticeAreasHeader()}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {renderPracticeAreas().map((area, index) => {
                const { cardClass, gradientClass, titleClass } = getPracticeAreaCardStyle(area, index);
                const linkTo = area.title === '가사 분쟁' ? '/landing-divorce' : '/practice-areas';

                return (
                  <Link
                    key={index}
                    to={linkTo}
                    className={cardClass}
                  >
                    <div className={gradientClass}></div>
                    <div className="p-6">
                      <h3 className={titleClass}>
                        {area.title}
                      </h3>
                      <p className="text-gray-600">{area.description}</p>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>

        {/* 성과 섹션 */}
        <section className="py-16 sm:py-24 bg-gradient-to-br from-[#2c1810] to-[#4a2c1a] relative overflow-hidden">
          <div className="absolute inset-0 bg-[url('/images/man_with_hammer.jpg')] opacity-5"></div>
          <div className="max-w-7xl mx-auto px-4 relative">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">휘상의 성과</h2>
              <p className="text-lg text-white/80 max-w-2xl mx-auto">쌓아온 신뢰와 성과</p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* 통계 카드 */}
              <div className="grid grid-cols-2 gap-6">
                {[
                  {
                    number: '1000+',
                    label: '사건 처리',
                    description: '다양한 분야의 사건 처리 경험',
                    icon: (
                      <svg
                        className="w-8 h-8"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path
                          d="M9 12H15M12 9V15M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    ),
                    color: '#e6a87c',
                  },
                  {
                    number: '98%',
                    label: '성공률',
                    description: '고객 만족도 기반 성공률',
                    icon: (
                      <svg
                        className="w-8 h-8"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path
                          d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    ),
                    color: '#d4a373',
                  },
                  {
                    number: '24/7',
                    label: '상담 지원',
                    description: '언제나 함께하는 법률 상담',
                    icon: (
                      <svg
                        className="w-8 h-8"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M12 8V12L14 14" strokeLinecap="round" strokeLinejoin="round" />
                        <circle cx="12" cy="12" r="10" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    ),
                    color: '#ccd5ae',
                  },
                  {
                    number: '100%',
                    label: '신뢰도',
                    description: '고객 신뢰도 평가',
                    icon: (
                      <svg
                        className="w-8 h-8"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path
                          d="M17 21V19C17 16.7909 14.3137 15 11 15C7.68629 15 5 16.7909 5 19V21M11 12C13.2091 12 15 10.2091 15 8C15 5.79086 13.2091 4 11 4C8.79086 4 7 5.79086 7 8C7 10.2091 8.79086 12 11 12Z"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    ),
                    color: '#fefae0',
                  },
                ].map((item, index) => (
                  <div
                    key={index}
                    className="group relative bg-white/10 backdrop-blur-sm rounded-2xl overflow-hidden hover:bg-white/20 transition-all duration-300"
                  >
                    <div className="p-6">
                      <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                        <div className="text-white">{item.icon}</div>
                      </div>
                      <div className="text-3xl font-bold text-white mb-2">{item.number}</div>
                      <div className="text-lg font-semibold text-white/90 mb-1">{item.label}</div>
                      <p className="text-white/70 text-sm">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* 차트 영역 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6">
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={[
                        { name: '형사', value: 85, color: '#e6a87c' },
                        { name: '민사', value: 92, color: '#d4a373' },
                        { name: '가사', value: 88, color: '#ccd5ae' },
                        { name: '금융', value: 95, color: '#fefae0' },
                        { name: '부동산', value: 90, color: '#e9edc9' },
                      ]}
                    >
                      <XAxis
                        dataKey="name"
                        stroke="#fff"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: '#fff', fontSize: 12 }}
                      />
                      <YAxis
                        stroke="#fff"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: '#fff', fontSize: 12 }}
                        domain={[0, 100]}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          border: 'none',
                          borderRadius: '8px',
                          boxShadow:
                            '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                        }}
                        labelStyle={{ color: '#2c1810', fontWeight: 'bold' }}
                        formatter={(value: number) => [`${value}%`, '성공률']}
                      />
                      <Bar
                        dataKey="value"
                        radius={[4, 4, 0, 0]}
                        barSize={24}
                        fill="#e6a87c"
                        background={{ fill: 'rgba(255, 255, 255, 0.1)', radius: 4 }}
                      >
                        {[
                          { name: '형사', value: 85, color: '#e6a87c' },
                          { name: '민사', value: 92, color: '#d4a373' },
                          { name: '가사', value: 88, color: '#ccd5ae' },
                          { name: '금융', value: 95, color: '#fefae0' },
                          { name: '부동산', value: 90, color: '#e9edc9' },
                        ].map((entry: { color: string }, index: number) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="text-center mt-6">
                  <p className="text-white/80 text-lg font-medium">분야별 성공률 (%)</p>
                  <div className="flex flex-wrap justify-center gap-4 mt-4">
                    {[
                      { name: '형사', color: '#e6a87c' },
                      { name: '민사', color: '#d4a373' },
                      { name: '가사', color: '#ccd5ae' },
                      { name: '금융', color: '#fefae0' },
                      { name: '부동산', color: '#e9edc9' },
                    ].map((item, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: item.color }}
                        />
                        <span className="text-white/70 text-sm">{item.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 카드 섹션 */}
        <section className="py-24 bg-white relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-[#fdfaf7] to-white"></div>
          <div className="max-w-7xl mx-auto px-4 relative">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">휘상의 약속</h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                최고의 법률 서비스를 제공하기 위한 우리의 노력
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                {
                  title: '전문 법률 상담',
                  description: '각 분야 전문 변호사의 맞춤형 법률 상담을 제공합니다.',
                  icon: (
                    <svg
                      className="w-8 h-8"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12 2L2 7L12 12L22 7L12 2Z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M2 17L12 22L22 17"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M2 12L12 17L22 12"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ),
                },
                {
                  title: '신속한 사건 처리',
                  description: '빠르고 정확한 법률 서비스로 고객의 시간을 소중히 여깁니다.',
                  icon: (
                    <svg
                      className="w-8 h-8"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12 8V12L14 14"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                    </svg>
                  ),
                },
                {
                  title: '합리적인 비용',
                  description: '투명한 비용 안내와 합리적인 수임료 체계를 운영합니다.',
                  icon: (
                    <svg
                      className="w-8 h-8"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12 1V23"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M17 5H9.5C8.57174 5 7.6815 5.36875 7.02513 6.02513C6.36875 6.6815 6 7.57174 6 8.5C6 9.42826 6.36875 10.3185 7.02513 10.9749C7.6815 11.6313 8.57174 12 9.5 12H14.5C15.4283 12 16.3185 12.3687 16.9749 13.0251C17.6313 13.6815 18 14.5717 18 15.5C18 16.4283 17.6313 17.3185 16.9749 17.9749C16.3185 18.6313 15.4283 19 14.5 19H6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ),
                },
                {
                  title: '풍부한 경험',
                  description: '다양한 사례 해결 경험을 바탕으로 최적의 해결책을 제시합니다.',
                  icon: (
                    <svg
                      className="w-8 h-8"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M19.4 15C19.2669 15.3016 19.2272 15.6362 19.286 15.9606C19.3448 16.285 19.4995 16.5843 19.73 16.82L19.79 16.88C19.976 17.0657 20.1235 17.2863 20.2241 17.5291C20.3248 17.7719 20.3766 18.0322 20.3766 18.295C20.3766 18.5578 20.3248 18.8181 20.2241 19.0609C20.1235 19.3037 19.976 19.5243 19.79 19.71C19.6043 19.896 19.3837 20.0435 19.1409 20.1441C18.8981 20.2448 18.6378 20.2966 18.375 20.2966C18.1122 20.2966 17.8519 20.2448 17.6091 20.1441C17.3663 20.0435 17.1457 19.896 16.96 19.71L16.9 19.65C16.6643 19.4195 16.365 19.2648 16.0406 19.206C15.7162 19.1472 15.3816 19.1869 15.08 19.32C14.7842 19.4468 14.532 19.6572 14.3543 19.9255C14.1766 20.1938 14.0813 20.5082 14.08 20.83V21C14.08 21.5304 13.8693 22.0391 13.4942 22.4142C13.1191 22.7893 12.6104 23 12.08 23C11.5496 23 11.0409 22.7893 10.6658 22.4142C10.2907 22.0391 10.08 21.5304 10.08 21V20.91C10.0723 20.579 9.96512 20.258 9.77251 19.9887C9.5799 19.7194 9.31074 19.5143 9 19.4C8.69838 19.2669 8.36381 19.2272 8.03941 19.286C7.71502 19.3448 7.41568 19.4995 7.18 19.73L7.12 19.79C6.93425 19.976 6.71368 20.1235 6.47088 20.2241C6.22808 20.3248 5.96783 20.3766 5.705 20.3766C5.44217 20.3766 5.18192 20.3248 4.93912 20.2241C4.69632 20.1235 4.47575 19.976 4.29 19.79C4.10405 19.6043 3.95653 19.3837 3.85588 19.1409C3.75523 18.8981 3.70343 18.6378 3.70343 18.375C3.70343 18.1122 3.75523 17.8519 3.85588 17.6091C3.95653 17.3663 4.10405 17.1457 4.29 16.96L4.35 16.9C4.58054 16.6643 4.73519 16.365 4.794 16.0406C4.85282 15.7162 4.81312 15.3816 4.68 15.08C4.55324 14.7842 4.34276 14.532 4.07447 14.3543C3.80618 14.1766 3.49179 14.0813 3.17 14.08H3C2.46957 14.08 1.96086 13.8693 1.58579 13.4942C1.21071 13.1191 1 12.6104 1 12.08C1 11.5496 1.21071 11.0409 1.58579 10.6658C1.96086 10.2907 2.46957 10.08 3 10.08H3.09C3.42099 10.0723 3.742 9.96512 4.0113 9.77251C4.28059 9.5799 4.48572 9.31074 4.6 9C4.73312 8.69838 4.77282 8.36381 4.714 8.03941C4.65519 7.71502 4.50054 7.41568 4.27 7.18L4.21 7.12C4.02405 6.93425 3.87653 6.71368 3.77588 6.47088C3.67523 6.22808 3.62343 5.96783 3.62343 5.705C3.62343 5.44217 3.67523 5.18192 3.77588 4.93912C3.87653 4.69632 4.02405 4.47575 4.21 4.29C4.39575 4.10405 4.61632 3.95653 4.85912 3.85588C5.10192 3.75523 5.36217 3.70343 5.625 3.70343C5.88783 3.70343 6.14808 3.75523 6.39088 3.85588C6.63368 3.95653 6.85425 4.10405 7.04 4.29L7.1 4.35C7.33568 4.58054 7.63502 4.73519 7.95941 4.794C8.28381 4.85282 8.61838 4.81312 8.92 4.68H9C9.29577 4.55324 9.54802 4.34276 9.72569 4.07447C9.90337 3.80618 9.99872 3.49179 10 3.17V3C10 2.46957 10.2107 1.96086 10.5858 1.58579C10.9609 1.21071 11.4696 1 12 1C12.5304 1 13.0391 1.21071 13.4142 1.58579C13.7893 1.96086 14 2.46957 14 3V3.09C14.0013 3.41179 14.0966 3.72618 14.2743 3.99447C14.452 4.26276 14.7042 4.47324 15 4.6C15.3016 4.73312 15.6362 4.77282 15.9606 4.714C16.285 4.65519 16.5843 4.50054 16.82 4.27L16.88 4.21C17.0657 4.02405 17.2863 3.87653 17.5291 3.77588C17.7719 3.67523 18.0322 3.62343 18.295 3.62343C18.5578 3.62343 18.8181 3.67523 19.0609 3.77588C19.3037 3.87653 19.5243 4.02405 19.71 4.21C19.896 4.39575 20.0435 4.61632 20.1441 4.85912C20.2448 5.10192 20.2966 5.36217 20.2966 5.625C20.2966 5.88783 20.2448 6.14808 20.1441 6.39088C20.0435 6.63368 19.896 6.85425 19.71 7.04L19.65 7.1C19.4195 7.33568 19.2648 7.63502 19.206 7.95941C19.1472 8.28381 19.1869 8.61838 19.32 8.92V9C19.4468 9.29577 19.6572 9.54802 19.9255 9.72569C20.1938 9.90337 20.5082 9.99872 20.83 10H21C21.5304 10 22.0391 10.2107 22.4142 10.5858C22.7893 10.9609 23 11.4696 23 12C23 12.5304 22.7893 13.0391 22.4142 13.4142C22.0391 13.7893 21.5304 14 21 14H20.91C20.5882 14.0013 20.2738 14.0966 20.0055 14.2743C19.7372 14.452 19.5268 14.7042 19.4 15Z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ),
                },
              ].map((item, index) => (
                <div
                  key={index}
                  className="group relative bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-[#b98e6a]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="p-8">
                    <div className="w-16 h-16 bg-[#b98e6a]/10 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                      <div className="text-[#b98e6a]">{item.icon}</div>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-4 group-hover:text-[#b98e6a] transition-colors duration-200">
                      {item.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 구조화된 데이터 마크업 */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(legalServiceData) }}
        />

        {/* 플로팅 버튼 그룹 */}
        <div className="fixed bottom-6 right-6 flex flex-col gap-3 z-50">
          {/* 전화 버튼 */}
          <a
            href="tel:02-6959-2170"
            className="w-14 h-14 rounded-full bg-[#b98e6a] text-white flex items-center justify-center shadow-lg hover:bg-[#a07a5a] transition-all duration-300"
            aria-label="전화 상담"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
          </a>

          {/* 카카오톡 버튼 */}
          <a
            href="https://pf.kakao.com/_DIVwn"
            target="_blank"
            rel="noopener noreferrer"
            className="w-14 h-14 rounded-full bg-[#FEE500] text-[#3A1D1D] flex items-center justify-center shadow-lg hover:bg-[#F6DC00] transition-all duration-300"
            aria-label="카카오톡 상담"
          >
            <svg className="w-7 h-7" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 3C7.03125 3 3 6.03125 3 9.90625C3 12.4687 4.73437 14.7344 7.40625 15.9844C7.17187 16.8281 6.46875 18.9219 6.32812 19.3594C6.14062 19.9219 6.5 19.9062 6.73438 19.7344C6.92188 19.6094 9.40625 17.9688 10.5938 17.1719C11.0469 17.2344 11.5156 17.2656 12 17.2656C16.9687 17.2656 21 14.2344 21 9.90625C21 6.03125 16.9687 3 12 3Z" />
            </svg>
          </a>

          {/* 상담 신청 버튼 */}
          <button
            onClick={() => setIsConsultModalOpen(true)}
            className="w-14 h-14 rounded-full bg-[#4a2c1a] text-white flex items-center justify-center shadow-lg hover:bg-[#5d3921] transition-all duration-300"
            aria-label="상담 신청"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
            </svg>
          </button>
        </div>

        {/* 상담 신청 모달 */}
        {isConsultModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden relative animate-fadeIn"
              onClick={(e) => e.stopPropagation()}
            >
              {/* 모달 헤더 */}
              <div className="bg-gradient-to-r from-[#4a2c1a] to-[#b98e6a] p-6 text-white">
                <h3 className="text-2xl font-bold">상담 신청</h3>
                <p className="text-white/80 mt-1">법무법인 휘상에서 전문적 상담을 받아보세요</p>
              </div>

              {/* 모달 내용 */}
              <Form method="post" className="p-6">
                <div className="space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                      이름
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={consultForm.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#b98e6a] focus:border-transparent"
                      placeholder="홍길동"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                      연락처
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={consultForm.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#b98e6a] focus:border-transparent"
                      placeholder="010-0000-0000"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                      문의내용
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={consultForm.message}
                      onChange={handleInputChange}
                      required
                      rows={4}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#b98e6a] focus:border-transparent resize-none"
                      placeholder="상담 받고 싶은 내용을 자세히 작성해주세요."
                    />
                  </div>
                </div>

                <div className="mt-6 flex items-center justify-between">
                  <button
                    type="button"
                    onClick={() => setIsConsultModalOpen(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100 focus:outline-none transition-colors duration-300"
                  >
                    취소
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-6 py-2 bg-gradient-to-r from-[#b98e6a] to-[#4a2c1a] rounded-md text-white hover:opacity-90 focus:outline-none transition-colors duration-300 disabled:opacity-70"
                  >
                    {isSubmitting ? '전송 중...' : '신청하기'}
                  </button>
                </div>

                <div className="mt-4 text-xs text-gray-500 text-center">
                  * 입력하신 정보는 상담 목적으로만 사용됩니다.
                </div>
              </Form>

              {/* 닫기 버튼 */}
              <button
                onClick={() => setIsConsultModalOpen(false)}
                className="absolute top-4 right-4 text-white hover:text-gray-200 focus:outline-none"
                aria-label="Close modal"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
